/**
 * RefundDestinationChoice — the three places a refund can go (BON-335): gift
 * passes or a gift card (whichever is switched on), the original card, or
 * another rail.
 *
 * Extracted as its own component (not a plain lowercase helper like the
 * drawer's other pieces) because it owns real interaction — a native radio
 * group with a controlled value — rather than rendering a fixed shape once.
 * `react/no-multi-comp` allows one component per file; this is that file's one.
 *
 * Native `<input type="radio">` sharing one `name` gets arrow-key navigation
 * and roving focus for free from the browser, so none of that is hand-rolled
 * here (BON-335 FE spec F8).
 */
import { CreditCard, Gift, Landmark, Ticket } from 'lucide-react';

import type { RefundMethod } from '../../api/bookingsV2';

/** The reason "Original card" is unavailable — reused verbatim as the F2
 *  disabled hint AND as the F7 post-submit `NO_PAYMENT_INTENT` message, so a
 *  race between stale client data and the server's own check reads as the
 *  same fact rather than two different sentences. */
export const CARD_UNAVAILABLE_REASON =
  "Not available — no card payment is on file for this booking, so there's nothing to refund it to directly.";

/** Why every money destination is unavailable on a booking with no cash on it
 *  (paid in full by gift card or gift passes): only passes can be issued. */
export const NO_CASH_REASON =
  'Not available — nothing was paid in cash, so only gift passes can be issued.';

/** Why each unavailable destination can't be chosen; absent means available. */
export type UnavailableReasons = Partial<Record<RefundMethod, string>>;

interface Destination {
  method: RefundMethod;
  label: string;
  description: string;
  icon: typeof Gift;
}

function giftPassDestination(seatCount: number): Destination {
  const passes = seatCount === 1 ? '1 gift pass' : `${seatCount} gift passes`;
  return {
    method: 'GIFT_PASS',
    label: 'Gift passes',
    description: `${passes}, one per seat. Each is good for one workshop admission.`,
    icon: Ticket,
  };
}

const GIFT_CARD_DESTINATION: Destination = {
  method: 'GIFT_CARD',
  // "Emailed" is deliberately left to the footer/receipt copy, which
  // reflects the SELECTED destination — all three rows render their
  // descriptions at once, so a static promise here would still be on
  // screen after the CSR picks a destination that makes no such promise.
  label: 'Bonsai Bar gift card',
  description: 'A store credit for the full amount, redeemable on any workshop.',
  icon: Gift,
};

const RECORDED_DESTINATIONS: Destination[] = [
  {
    method: 'CARD',
    label: 'Original card',
    description: 'Already refunded by hand in Stripe. Nothing is charged from here.',
    icon: CreditCard,
  },
  {
    method: 'EXTERNAL',
    label: 'Other',
    description: 'A check, a transfer, or another rail — already sent outside Nebari.',
    icon: Landmark,
  },
];

function destinationOption(
  destination: Destination,
  selected: boolean,
  disabled: boolean,
  disabledReason: string | null,
  onChange: (method: RefundMethod) => void,
) {
  const { method, label, description, icon: Icon } = destination;
  const inputId = `refund-destination-${method}`;
  const descriptionId = `${inputId}-description`;
  const reasonId = `${inputId}-reason`;
  const blockedByReason = disabled && disabledReason;
  return (
    <label
      key={method}
      htmlFor={inputId}
      // `focus-within:ring-*` gives the row a focus cue even though its only
      // focusable element (the input below) is visually hidden — WCAG 2.4.7
      // (B6). `:focus-within` is valid here because the label is the input's
      // ANCESTOR; an earlier version used `peer-focus-visible:`, which
      // compiles to a sibling combinator that a parent can never match
      // against its own child — that ring could never render under any
      // condition, and shipped anyway because the test asserted a class name
      // rather than the DOM relationship the variant depends on.
      className={`flex items-start gap-3 rounded-lg border px-3 py-3 focus-within:ring-2 focus-within:ring-moss-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-[#1a1f1c] ${
        selected
          ? 'border-moss-400 bg-moss-50/60 dark:border-moss-700 dark:bg-moss-900/10'
          : 'border-stone-200 dark:border-stone-700'
      } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <input
        type="radio"
        id={inputId}
        name="refund-destination"
        value={method}
        checked={selected}
        disabled={disabled}
        // Overrides the wrapping `<label>`'s text as the accessible name (accname
        // spec: an element's own `aria-label` wins over an associated label's
        // content) so the name is just "Original card", not that plus its
        // description and disabled-reason paragraphs concatenated onto it.
        aria-label={label}
        // Every option gets a description reference (B9) — not only the
        // disabled one — so a screen-reader user hears why each destination
        // exists, not just why one of them is unavailable.
        aria-describedby={blockedByReason ? reasonId : descriptionId}
        onChange={() => onChange(method)}
        // Visually hidden, not hidden from focus or the accessibility tree —
        // `:focus-within` on the wrapping label above is what turns this
        // input receiving focus into a visible ring on the row.
        className="sr-only"
      />
      <span
        className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-moss-700 dark:bg-[#1e2521] dark:text-moss-300 ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        <Icon size={16} />
      </span>
      <div>
        <p className="text-sm font-semibold text-bark-900 dark:text-stone-100">{label}</p>
        <p id={descriptionId} className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
          {blockedByReason ? '' : description}
        </p>
        {blockedByReason && (
          <p id={reasonId} className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
            {disabledReason}
          </p>
        )}
      </div>
    </label>
  );
}

/** Gift passes replace the gift card while they are switched on; until the
 *  setting loads, neither is shown. */
function destinationsFor(giftPassesEnabled: boolean | null, seatCount: number): Destination[] {
  if (giftPassesEnabled === null) return RECORDED_DESTINATIONS;
  const minting = giftPassesEnabled ? giftPassDestination(seatCount) : GIFT_CARD_DESTINATION;
  return [minting, ...RECORDED_DESTINATIONS];
}

export function RefundDestinationChoice({
  value,
  onChange,
  unavailableReasons,
  disabled,
  giftPassesEnabled,
  seatCount,
}: {
  value: RefundMethod;
  onChange: (method: RefundMethod) => void;
  /** Null while the setting loads. */
  giftPassesEnabled: boolean | null;
  /** Seats on the booking — one gift pass each. */
  seatCount: number;
  /** Each listed destination is disabled with its reason shown beneath it —
   *  e.g. "Original card" when the booking has no Stripe charge. */
  unavailableReasons: UnavailableReasons;
  /** True while a request is in flight — disables every option, matching the
   *  rest of the form. */
  disabled: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Refund destination" className="space-y-2">
      {giftPassesEnabled === null && (
        <p className="text-xs text-stone-500 dark:text-stone-400">Loading refund options…</p>
      )}
      {destinationsFor(giftPassesEnabled, seatCount).map((destination) => {
        const blockedReason = unavailableReasons[destination.method] ?? null;
        return destinationOption(
          destination,
          value === destination.method,
          disabled || blockedReason !== null,
          blockedReason,
          onChange,
        );
      })}
    </div>
  );
}
